/**
 * skaile asset — author and install AI assets.
 *
 * Subcommands:
 *   migrate <path>             move/copy/link a workspace asset into a library
 *   install <ref>              install an asset from a library into a workspace
 *   list                       synonym for `skaile list` (declared deps + state)
 *   show <ref>                 inspect an asset by canonical ref
 *
 * The former `asset publish` stub has moved to `library publish` so the push
 * + register flow is atomic with the underlying git push. Asset creation
 * lives under `library create <kind> <name>`.
 *
 * @docLink cli/commands/asset
 */

import * as fs from "node:fs";
import * as path from "node:path";
import {
  checkAgentSkillConformance,
  isValidAssetName,
  parseFrontmatter,
} from "@skaile/workspaces/core";
import { resolveLibraryDir } from "@skaile/workspaces/library";
import { Command } from "commander";
import { logErr, logInfo, logOk, logWarn, S } from "../helpers.ts";
import { openLibraryManager } from "../open-library.ts";
import { makeListCommand } from "./catalog.ts";
import {
  ensureDomain,
  normalizeSkillContent,
  resolveImportSource,
  upsertManifestAsset,
} from "./skailify-lib.ts";

export function makeAssetCommand(): Command {
  const cmd = new Command("asset").description("Author and install AI assets");

  // ── asset import ───────────────────────────────────────────────────────────
  // High-level "skailify" path: `asset migrate` wrapped with the frontmatter
  // normalize + domain scaffold + manifest-wire steps the raw byte-copy skips.
  cmd
    .command("import <name-or-path>")
    .description("Import + skailify an existing coding-agent skill into a library")
    .option("--to <library>", "Target library (default: auto / personal)")
    .option("--domain <name>", "Place under this domain's skills/ (created if missing)")
    .option("--version <semver>", "Version to stamp when the skill ships none")
    .option("--stage <stage>", "Lifecycle stage to stamp (default: alpha)")
    .option("--no-normalize", "Copy verbatim — skip frontmatter normalization")
    .option("--commit", "Auto-commit in git-backed libraries", false)
    .action(
      async (
        nameOrPath: string,
        opts: {
          to?: string;
          domain?: string;
          version?: string;
          stage?: string;
          normalize: boolean;
          commit: boolean;
        },
      ) => {
        const src = resolveImportSource(nameOrPath);
        if (!src) {
          logErr(
            `No skill found for "${nameOrPath}". Pass a path to a SKILL.md dir, ` +
              "or a bare name present under ~/.claude/skills/.",
          );
          process.exit(1);
        }
        logInfo(`Source: ${S.dim(src.dir)} (${src.via})`);

        const { manager, close } = await openLibraryManager();
        try {
          // Resolve target library (mirrors `asset migrate`).
          let target: Awaited<ReturnType<typeof manager.requireLibrary>>;
          if (opts.to) {
            target = await manager.requireLibrary(opts.to);
          } else {
            const all = await manager.listLibraries();
            const def = all.find(
              (l) => l.isDefault && (l.ownership === "owner" || l.ownership === "contributor"),
            );
            target = def ?? (await manager.ensurePersonalLibrary(resolveLibraryDir())).library;
          }
          if (target.ownership === "reader") {
            logErr(`Library "${target.name}" is read-only (ownership=reader). Choose --to.`);
            process.exit(1);
          }

          // Normalize the SKILL.md (unless --no-normalize).
          const rawSkill = fs.readFileSync(src.skillFile, "utf8");
          const norm = opts.normalize
            ? normalizeSkillContent(rawSkill, {
                fallbackName: path.basename(src.dir),
                version: opts.version,
                stage: opts.stage,
              })
            : { content: rawSkill, name: path.basename(src.dir), changed: false, notes: [] };
          if (!opts.normalize && !isValidAssetName(norm.name)) {
            logWarn(
              `Name "${norm.name}" is not canonical (kebab-case); ` +
                "`skaile validate` will reject this manifest entry. Drop --no-normalize to slugify it.",
            );
          }

          // Resolve the destination (scaffolding the domain on demand).
          let destParent = target.path;
          let publisher: string | undefined;
          if (opts.domain) {
            const dom = ensureDomain(target.path, opts.domain);
            if (dom.created) logOk(`Scaffolded domain ${S.cmd(opts.domain)} → ${dom.dir}`);
            destParent = path.join(dom.dir, "skills");
            publisher = dom.publisher;
          }
          fs.mkdirSync(destParent, { recursive: true });
          const destDir = path.join(destParent, norm.name);
          if (fs.existsSync(destDir)) {
            logErr(`Destination already exists: ${destDir}`);
            process.exit(1);
          }

          // Copy the asset, then overwrite SKILL.md with the normalized content.
          fs.cpSync(src.dir, destDir, { recursive: true });
          fs.writeFileSync(path.join(destDir, "SKILL.md"), norm.content, "utf8");
          for (const note of norm.notes) logInfo(`  • ${note}`);

          // Auto-wire the manifest entry. Re-read the description from the
          // written frontmatter rather than re-parsing via regex.
          const fm = parseFrontmatter(norm.content).data;
          const descRaw = fm.description;
          const description = typeof descRaw === "string" ? descRaw.trim() : undefined;

          // Advisory: flag any agentskills.io standard divergences (dest dir is
          // norm.name, so the name==dir rule passes by construction).
          for (const f of checkAgentSkillConformance(fm, { dirName: norm.name })) {
            logWarn(`conformance: ${f.field} — ${f.message}`);
          }
          const root = path.relative(target.path, destDir);
          const up = upsertManifestAsset(target.path, {
            kind: "skill",
            name: norm.name,
            root,
            version: opts.version,
            publisher,
            description,
          });
          logOk(`${up.added ? "Added" : "Updated"} manifest entry skill:${norm.name}`);

          if (target.backend === "git" && opts.commit) {
            const { spawnSync } = await import("node:child_process");
            spawnSync("git", ["add", "--", destDir], { cwd: target.path, stdio: "inherit" });
            const r = spawnSync("git", ["commit", "-m", `skaile: import ${norm.name}`], {
              cwd: target.path,
              stdio: "inherit",
            });
            if (r.status !== 0) {
              logWarn("Nothing committed (working tree clean or commit aborted).");
            }
          }

          logOk(`Imported ${S.cmd(norm.name)} → ${target.name}`);
          logInfo(`Verify: ${S.cmd(`skaile validate ${target.path}`)}`);
        } finally {
          close();
        }
      },
    );

  // ── asset migrate ────────────────────────────────────────────────────────
  cmd
    .command("migrate <path>")
    .description("Move/copy/link a workspace asset into a library")
    .option("--to <library>", "Target library (default: auto)")
    .option("--domain <name>", "Place inside this domain (domain-structured libs)")
    .option("--mode <mode>", "copy | move | link", "copy")
    .option("--commit", "Auto-commit in git-backed libraries", false)
    .action(
      async (
        srcPath: string,
        opts: {
          to?: string;
          domain?: string;
          mode: "copy" | "move" | "link";
          commit: boolean;
        },
      ) => {
        if (!["copy", "move", "link"].includes(opts.mode)) {
          logErr(`Invalid --mode "${opts.mode}". Expected: copy | move | link.`);
          process.exit(1);
        }
        if (!fs.existsSync(srcPath)) {
          logErr(`Source path does not exist: ${srcPath}`);
          process.exit(1);
        }
        const { manager, close } = await openLibraryManager();
        try {
          // Resolve target library.
          let target: Awaited<ReturnType<typeof manager.requireLibrary>>;
          if (opts.to) {
            target = await manager.requireLibrary(opts.to);
          } else {
            const all = await manager.listLibraries();
            const def = all.find(
              (l) => l.isDefault && (l.ownership === "owner" || l.ownership === "contributor"),
            );
            if (def) {
              target = def;
            } else {
              // Auto-init personal silently.
              const res = await manager.ensurePersonalLibrary(resolveLibraryDir());
              target = res.library;
            }
          }
          if (target.ownership === "reader") {
            logErr(
              `Library "${target.name}" is read-only (ownership=reader). Choose another with --to.`,
            );
            process.exit(1);
          }
          // Destination path.
          const basename = path.basename(srcPath);
          const destDir =
            target.structure === "domain" && opts.domain
              ? path.join(target.path, opts.domain)
              : target.path;
          fs.mkdirSync(destDir, { recursive: true });
          const destPath = path.join(destDir, basename);
          if (fs.existsSync(destPath)) {
            logErr(`Destination already exists: ${destPath}`);
            process.exit(1);
          }

          switch (opts.mode) {
            case "copy":
              fs.cpSync(srcPath, destPath, { recursive: true });
              break;
            case "move":
              fs.renameSync(srcPath, destPath);
              break;
            case "link":
              fs.symlinkSync(path.resolve(srcPath), destPath);
              break;
          }

          if (target.backend === "git" && opts.commit) {
            const { spawnSync } = await import("node:child_process");
            spawnSync("git", ["add", "--", destPath], { cwd: target.path, stdio: "inherit" });
            const r = spawnSync("git", ["commit", "-m", `skaile: migrate ${basename}`], {
              cwd: target.path,
              stdio: "inherit",
            });
            if (r.status !== 0) {
              logWarn("Nothing committed (working tree clean or commit aborted).");
            }
          }

          logOk(`Migrated ${basename} → ${target.name} (${opts.mode})`);
          if (opts.mode === "move") {
            logInfo("Source moved into the library — future edits modify the library copy.");
          } else if (opts.mode === "copy") {
            logInfo("Original kept; workspace edits will NOT reach the library.");
          } else {
            logInfo("Symlinked — workspace edits propagate to the library immediately.");
          }
        } finally {
          close();
        }
      },
    );

  // ── asset install ────────────────────────────────────────────────────────
  cmd
    .command("install <ref>")
    .description("Install an asset from a library into a workspace")
    .option("--target <dir>", "Workspace target (default: cwd)")
    .option("--mode <mode>", "copy | link", "copy")
    .option("--from <library>", "Library to install from (default: any)")
    .action(
      async (ref: string, opts: { target?: string; mode: "copy" | "link"; from?: string }) => {
        if (!["copy", "link"].includes(opts.mode)) {
          logErr(`Invalid --mode "${opts.mode}". Expected: copy | link.`);
          process.exit(1);
        }
        const { manager, library, close } = await openLibraryManager();
        try {
          const def = await library.getAssetDef(ref);
          if (!def) {
            logErr(`No asset found for ref "${ref}". Try \`skaile search\`.`);
            process.exit(1);
          }
          const target = opts.target ?? process.cwd();
          const lib = def.libraryId
            ? (await manager.listLibraries()).find((l) => l.id === def.libraryId)
            : undefined;
          if (!lib) {
            logErr(`Asset has no associated library; install path unknown.`);
            process.exit(1);
          }
          if (opts.from && lib.name !== opts.from) {
            logWarn(`Asset belongs to "${lib.name}", but --from=${opts.from} was specified.`);
          }
          // Discover asset's on-disk path via manifest convention.
          // For MVP, we assume the asset's `manifest.path` field if present, else `<library>/<name>`.
          const manifestPath = (def.manifest as Record<string, unknown>).path;
          const candidateSubpath =
            typeof manifestPath === "string" && manifestPath.length > 0 ? manifestPath : def.name;
          const srcPath = path.join(lib.path, candidateSubpath);
          if (!fs.existsSync(srcPath)) {
            logErr(`Asset path not found: ${srcPath}`);
            process.exit(1);
          }
          const destPath = path.join(target, candidateSubpath);
          fs.mkdirSync(path.dirname(destPath), { recursive: true });
          if (opts.mode === "copy") {
            fs.cpSync(srcPath, destPath, { recursive: true });
          } else {
            fs.symlinkSync(path.resolve(srcPath), destPath);
          }
          logOk(`Installed ${ref} → ${destPath} (${opts.mode})`);
        } finally {
          close();
        }
      },
    );

  // ── asset list ───────────────────────────────────────────────────────────
  // Synonym for the top-level `skaile list`: declared deps from skaile.yaml +
  // reconciliation state. The old library-index browse view moved to `skaile search`.
  cmd.addCommand(makeListCommand());

  // ── asset show ───────────────────────────────────────────────────────────
  cmd
    .command("show <ref>")
    .description("Show details for an asset by canonical ref")
    .action(async (ref: string) => {
      const { library, close } = await openLibraryManager();
      try {
        const def = await library.getAssetDef(ref);
        if (!def) {
          logErr(`Not found: ${ref}`);
          process.exit(1);
        }
        console.log(JSON.stringify(def, null, 2));
      } finally {
        close();
      }
    });

  return cmd;
}
